"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { API_URL } from "../config"
import MeteorologicalDataInput from "../components/calculator/MeteorologicalDataInput"
import WeatherDataDisplay from "../components/calculator/WeatherDataDisplay"
import ModelSelector from "../components/calculator/ModelSelector"
import ResultsTable from "../components/calculator/ResultsTable"
import ResultsChart from "../components/calculator/ResultsChart"
import WeatherUploader from "../components/calculator/WeatherUploader"
import { useLanguage } from "../context/LanguageContext"
import WeatherSummary from "../components/weather/WeatherSummary"
import WeatherRangeChart from "../components/weather/WeatherRangeChart"
import jsPDF from "jspdf"
import "jspdf-autotable"
import "./Home.css"
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps"

const GOOGLE_MAPS_API_KEY = "AIzaSyCT_w1O7dUdXlSvWCRn-DxU8e11Vd0t94k"

const Home = () => {
  const { t, direction } = useLanguage()

  // Location state
  const [countries, setCountries] = useState([])
  const [districts, setDistricts] = useState([])
  const [selectedCountry, setSelectedCountry] = useState("")
  const [selectedDistrict, setSelectedDistrict] = useState("")
  const [coordinates, setCoordinates] = useState(null)
  const [locationName, setLocationName] = useState("")

  // Date state
  const [dateType, setDateType] = useState("single")
  const [singleDate, setSingleDate] = useState(new Date().toISOString().split("T")[0])
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0])
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0])

  // Data state
  const [weatherData, setWeatherData] = useState(null)
  const [forecastData, setForecastData] = useState(null)
  const [uploadedWeatherData, setUploadedWeatherData] = useState(null)
  const [meteorologicalData, setMeteorologicalData] = useState({
    tempMax: "",
    tempMin: "",
    tempAvg: "",
    temperature: "",
    feelsLike: "",
    stationFetch: "",
    humidity: "",
    humidityMax: "",
    humidityMin: "",
    uz: "",
    ud: "",
    un: "",
    zw: "",
    windSpeed: "",
    zp: "",
    solarRadiation: "",
    n: "",
    rs: "",
  })

  // UI state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [results, setResults] = useState(null)
  const [selectedModels, setSelectedModels] = useState({
    general: ["general-penman-monteith"],
    alfalfa: [],
    grass: [],
  })
  const [hcref, setHcref] = useState(0.2)

  // Map state
  const [mapCenter, setMapCenter] = useState({ lat: 20, lng: 70 })
  const [mapZoom, setMapZoom] = useState(5)

  // Optimized: Fetch countries on mount
  useEffect(() => {
    axios
      .get(`${API_URL}/api/locations/countries`)
      .then((res) => setCountries(res.data))
      .catch((err) => {
        setError("Failed to fetch countries")
        console.error(err)
      })
  }, [])

  // Optimized: Fetch districts when country changes
  useEffect(() => {
    if (!selectedCountry) {
      setDistricts([])
      return
    }
    axios
      .get(`${API_URL}/api/locations/districts/${selectedCountry}`)
      .then((res) => setDistricts(res.data))
      .catch((err) => {
        setError("Failed to fetch districts")
        console.error(err)
      })
  }, [selectedCountry])

  // Optimized: Get user's current location on initial load and fetch all relevant data once
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          setCoordinates(coords)
          setMapCenter(coords)
          setMapZoom(12)
        },
        (error) => console.error("Error getting current location:", error),
      )
    }
    // eslint-disable-next-line
  }, [])

  // Fetch weather data only on country/district/date change (not on coordinates)
  useEffect(() => {
    if (!selectedCountry || !selectedDistrict) return
    fetchWeatherByLocation()
    // eslint-disable-next-line
  }, [selectedCountry, selectedDistrict, dateType, singleDate, startDate, endDate])

  // Fetch weather by coordinates only when coordinates change (e.g. map click or geolocation)
  useEffect(() => {
    if (!coordinates) return
    fetchWeatherByCoordinates()
    // eslint-disable-next-line
  }, [coordinates, dateType, singleDate, startDate, endDate])

  // Fetch weather by country/district
  const fetchWeatherByLocation = async () => {
    setLoading(true)
    setError(null)
    let url = `${API_URL}/api/weather/?country=${selectedCountry}&district=${selectedDistrict}`
    if (dateType === "single") {
      url += `&date=${singleDate}`
    } else {
      url += `&startDate=${startDate}&endDate=${endDate}`
    }
    try {
      const res = await axios.get(url)
      setWeatherData(res.data)
      setForecastData(null)
      setMeteorologicalData(res.data)
      setLocationName("")
    } catch (err) {
      setError("Failed to fetch weather data")
      setWeatherData(null)
      setForecastData(null)
      setMeteorologicalData({})
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch weather by coordinates (and reverse geocode)
  const fetchWeatherByCoordinates = async () => {
    setLoading(true)
    setError(null)
    // 1. Reverse geocode for location name
    try {
      const res = await axios.get(`${API_URL}/api/reverse-geocode`, {
        params: { lat: coordinates.lat, lon: coordinates.lng },
      })
      if (res.data.results?.length > 0) {
        const result = res.data.results[0]
        setLocationName(result.formatted_address)
        // Optionally extract district/country but do not trigger weather fetch by location
      }
    } catch (err) {
      setLocationName("")
    }
    // 2. Fetch weather by coordinates
    let url = `${API_URL}/api/weather/co-ordinates?lat=${coordinates.lat}&lng=${coordinates.lng}`
    if (dateType === "single") {
      url += `&date=${singleDate}`
    } else {
      url += `&startDate=${startDate}&endDate=${endDate}`
    }
    try {
      const res = await axios.get(url)
      setWeatherData(res.data)
      setMeteorologicalData(res.data)
      // Optionally fetch forecast for single date
      if (dateType === "single") {
        setForecastData(res.data)
      } else {
        setForecastData(null)
      }
    } catch (err) {
      setError("Failed to fetch weather data")
      setWeatherData(null)
      setForecastData(null)
      setMeteorologicalData({})
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value)
    setSelectedDistrict("")
    setCoordinates(null)
    setLocationName("")
    setWeatherData(null)
    setForecastData(null)
  }

  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value)
    setCoordinates(null)
    setLocationName("")
    setWeatherData(null)
    setForecastData(null)
  }

  const handleCoordinatesSelect = async (coords) => {
    setCoordinates(coords)
    setSelectedCountry("")
    setSelectedDistrict("")
    setWeatherData(null)
    setForecastData(null)
    setLocationName("")
  }

  const handleMapClick = (e) => {
    const clickedLocation = {
      lat: e.detail.latLng.lat,
      lng: e.detail.latLng.lng,
    }
    handleCoordinatesSelect(clickedLocation)
  }

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert(t("Geolocation is not supported by this browser."))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }
        handleCoordinatesSelect(coords)
      },
      (error) => {
        console.error("Error getting location:", error)
        alert(t("Unable to retrieve your location. Please check your browser permissions."))
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
    )
  }

  const handleModelChange = (category, model, checked) => {
    setSelectedModels((prev) => ({
      ...prev,
      [category]: checked ? [...prev[category], model] : prev[category].filter((m) => m !== model),
    }))
  }

  const handleHcrefChange = (value) => {
    setHcref(value)
  }

  const calculateETref = async () => {
    try {
      setLoading(true)
      setError(null)

      const payload = {
        weatherData: uploadedWeatherData || weatherData,
        meteorologicalData,
        models: selectedModels,
        hcref,
        location: coordinates ? { coordinates } : { country: selectedCountry, district: selectedDistrict },
        dateType,
        date: dateType === "single" ? singleDate : { start: startDate, end: endDate },
      }

      const res = await axios.post(`${API_URL}/api/etref/calculate`, payload)
      setResults(res.data)
    } catch (err) {
      setError("Failed to calculate WATref")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const exportToPDF = () => {
    if (!results) return

    try {
      const doc = new jsPDF()

      // Add title
      doc.setFontSize(18)
      doc.text("WATref Calculation Results", 14, 22)

      // Add location info
      doc.setFontSize(12)
      let locationText = "Location: "
      if (locationName) {
        locationText += locationName
      } else if (coordinates) {
        locationText += `Lat: ${coordinates.lat.toFixed(4)}, Lng: ${coordinates.lng.toFixed(4)}`
      } else if (selectedCountry && selectedDistrict) {
        const country = countries.find((c) => c._id === selectedCountry)
        const district = districts.find((d) => d._id === selectedDistrict)
        locationText += `${district?.name || ""}, ${country?.name || ""}`
      }
      doc.text(locationText, 14, 32)

      // Add date info
      const dateText =
        dateType === "single"
          ? `Date: ${new Date(singleDate).toLocaleDateString()}`
          : `Date: ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`
      doc.text(dateText, 14, 40)

      // Add models info
      doc.text("Models:", 14, 48)
      const modelNames = results.models.map((model) => model.name).join(", ")
      doc.text(modelNames, 14, 56)

      // Add results table
      const tableColumn = ["Date", ...results.models.map((model) => model.name)]
      const tableRows = results.data.map((dayResult) => [
        new Date(dayResult.date).toLocaleDateString(),
        ...results.models.map((model) => {
          const value = dayResult.etref[model.id]
          return value !== undefined ? value.toFixed(2) : "N/A"
        }),
      ])

      // Add averages
      const averages = [
        "Average",
        ...results.models.map((model) => {
          const values = results.data.map((day) => day.etref[model.id]).filter((val) => val !== undefined)
          return values.length > 0 ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2) : "N/A"
        }),
      ]
      tableRows.push(averages)

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 65,
        theme: "grid",
        styles: { fontSize: 10 },
        headStyles: { fillColor: [76, 175, 80] },
      })

      // Add weather data summary
      const finalY = doc.lastAutoTable.finalY || 65
      doc.text("Weather Data Summary:", 14, finalY + 10)

      const weatherRows = []
      if (weatherData) {
        if (Array.isArray(weatherData)) {
          const avgTemp =
            weatherData.reduce((sum, day) => sum + (day.tempMax + day.tempMin) / 2, 0) / weatherData.length
          const avgHumidity = weatherData.reduce((sum, day) => sum + day.humidity, 0) / weatherData.length
          const avgWindSpeed = weatherData.reduce((sum, day) => sum + day.windSpeed, 0) / weatherData.length

          weatherRows.push(
            ["Average Temperature", `${avgTemp.toFixed(1)} °C`],
            ["Average Humidity", `${avgHumidity.toFixed(1)} %`],
            ["Average Wind Speed", `${avgWindSpeed.toFixed(1)} m/s`],
          )
        } else {
          weatherRows.push(
            ["Max Temperature", `${weatherData.tempMax} °C`],
            ["Min Temperature", `${weatherData.tempMin} °C`],
            ["Humidity", `${weatherData.humidity} %`],
            ["Wind Speed", `${weatherData.windSpeed} m/s`],
          )
        }

        doc.autoTable({
          body: weatherRows,
          startY: finalY + 15,
          theme: "grid",
          styles: { fontSize: 10 },
        })
      }

      // Add meteorological data summary
      if (meteorologicalData) {
        const meteoY = doc.lastAutoTable.finalY || finalY + 15
        doc.text("Meteorological Data Summary:", 14, meteoY + 10)

        const fieldMap = {
          tempMax: "Max Temperature",
          tempMin: "Min Temperature",
          tempAvg: "Avg Temperature",
          temperature: "Temperature",
          feelsLike: "Feels Like",
          humidity: "Humidity",
          humidityMax: "Max Humidity",
          humidityMin: "Min Humidity",
          uz: "Wind Speed (uz)",
          ud: "Wind Speed (ud)",
          un: "Wind Speed (un)",
          zw: "Wind Direction (zw)",
          windSpeed: "Wind Speed",
          zp: "Wind Pressure (zp)",
          solarRadiation: "Solar Radiation",
          n: "Sunshine Duration (n)",
          rs: "Solar Radiation (rs)",
          stationFetch: "Station Info",
        }

        const meteoRows = Object.entries(fieldMap)
          .filter(([key]) => meteorologicalData[key] && meteorologicalData[key] !== "")
          .map(([key, label]) => [label, `${meteorologicalData[key]}`])

        if (meteoRows.length > 0) {
          doc.autoTable({
            body: meteoRows,
            startY: meteoY + 15,
            theme: "grid",
            styles: { fontSize: 10 },
          })
        }
      }

      // Add footer
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(10)
        doc.text(
          `Generated on ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: "center" },
        )
      }

      doc.save(`WATref_Calculation_${new Date().toISOString().split("T")[0]}.pdf`)
    } catch (err) {
      console.error("Error generating PDF:", err)
      alert("Failed to generate PDF. Please try again.")
    }
  }

  return (
    <div className="home-container" dir={direction}>
      {/* Header Section */}
      <div className="header-section">
        <h1>{t("Reference Evapotranspiration Calculator")}</h1>
        <p>{t("Calculate WATref using different models and weather data")}</p>
      </div>

      {/* Location and Weather Display Section */}
      <div className="top-section">
        <div className="location-form-section">
          <div className="location-form-card">
            <div className="location-form-header">
              <h3>{t("Select Location")}</h3>
            </div>
            <div className="location-form-body">
              <div className="location-inputs">
                <div className="input-row">
                  <div className="input-group">
                    <label>{t("Country")}</label>
                    <select value={selectedCountry} onChange={handleCountryChange} className="form-input">
                      <option value="">{t("Select Country")}</option>
                      {countries.map((country) => (
                        <option key={country._id} value={country._id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="input-group">
                    <label>{t("City/District")}</label>
                    <select
                      value={selectedDistrict}
                      onChange={handleDistrictChange}
                      className="form-input"
                      disabled={!selectedCountry}
                    >
                      <option value="">{t("Select City")}</option>
                      {districts.map((district) => (
                        <option key={district._id} value={district._id}>
                          {district.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="current-location-btn-container">
                  <button className="current-location-btn" onClick={getCurrentLocation} disabled={loading}>
                    <i className="fas fa-location-arrow"></i>
                    {loading ? t("Getting Location...") : t("Use Current Location")}
                  </button>
                </div>

                {coordinates && (
                  <div className="coordinates-display">
                    <p>
                      <strong>{t("Selected Location")}:</strong>
                      <br />
                      {locationName || `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`}
                    </p>
                  </div>
                )}
              </div>

              <div className="date-selection">
                <div className="date-type-selector">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="dateType"
                      value="single"
                      checked={dateType === "single"}
                      onChange={() => setDateType("single")}
                    />
                    <span>{t("Single Date")}</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="dateType"
                      value="range"
                      checked={dateType === "range"}
                      onChange={() => setDateType("range")}
                    />
                    <span>{t("Date Range")}</span>
                  </label>
                </div>

                <div className="date-inputs">
                  {dateType === "single" ? (
                    <div className="input-group">
                      <input
                        type="date"
                        value={singleDate}
                        onChange={(e) => setSingleDate(e.target.value)}
                        className="form-input"
                      />
                    </div>
                  ) : (
                    <div className="date-range-inputs">
                      <div className="input-group">
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="form-input"
                        />
                      </div>
                      <div className="input-group">
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="form-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="weather-display-section">
          {weatherData && dateType === "single" && (
            <WeatherSummary weatherData={weatherData} forecastData={forecastData} />
          )}
          {weatherData && dateType === "range" && Array.isArray(weatherData) && (
            <WeatherRangeChart
              weatherData={weatherData}
              startDate={startDate}
              endDate={endDate}
              locationName={locationName}
              coordinates={coordinates}
              selectedCountry={selectedCountry}
              selectedDistrict={selectedDistrict}
              countries={countries}
              districts={districts}
            />
          )}
          {!weatherData && !loading && (
            <div className="weather-placeholder">
              <div className="weather-placeholder-content">
                <i className="fas fa-cloud-sun weather-placeholder-icon"></i>
                <p>{t("Select location and date to view weather data")}</p>
              </div>
            </div>
          )}
          {loading && (
            <div className="weather-loading">
              <div className="spinner"></div>
              <p>{t("Loading weather data...")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Google Maps Section */}
      <div className="map-section">
        <div className="map-container">
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <Map
              center={mapCenter}
              zoom={mapZoom}
              gestureHandling="greedy"
              disableDefaultUI={false}
              zoomControl={true}
              mapTypeControl={true}
              streetViewControl={true}
              fullscreenControl={true}
              style={{ width: "100%", height: "100%" }}
              onClick={handleMapClick}
              onZoomChanged={(e) => setMapZoom(e.detail.zoom)}
              onCenterChanged={(e) => {
                const center = e.detail.center
                setMapCenter({ lat: center.lat, lng: center.lng })
              }}
            >
              {coordinates && <Marker position={coordinates} />}
            </Map>
          </APIProvider>
        </div>
      </div>

      {/* Calculator Grid */}
      <div className="calculator-grid">
        {/* Meteorological Data Input */}
        {/* <div className="meteo-section">
          <MeteorologicalDataInput
            meteorologicalData={meteorologicalData}
            setMeteorologicalData={setMeteorologicalData}
          />
        </div> */}

        {/* Weather Data Upload */}
        <div className="upload-section">
          <div className="card">
            <div className="card-header">
              <h2>{t("Upload Weather Data")}</h2>
            </div>
            <div className="card-body">
              <WeatherUploader
                onUpload={setUploadedWeatherData}
                onDataMapping={setMeteorologicalData}
                defaultLocation={{
                  country: selectedCountry,
                  district: selectedDistrict,
                  coordinates: coordinates,
                  locationName: locationName,
                }}
              />
            </div>
          </div>
        </div>

        {/* Weather Data Display - Only show for date range */}
        {dateType === "range" && (
          <div className="weather-section">
            <div className="card">
              <div className="card-header">
                <h2>{t("Weather Data")}</h2>
              </div>
              <div className="card-body">
                <WeatherDataDisplay weatherData={weatherData} loading={loading} error={error} />
              </div>
            </div>
          </div>
        )}

        {/* WATref Models */}
        <div className="models-section">
          <div className="card">
            <div className="card-header">
              <h2>{t("WATref Calculation Models")}</h2>
            </div>
            <div className="card-body">
              <ModelSelector
                selectedModels={selectedModels}
                onChange={handleModelChange}
                hcref={hcref}
                onHcrefChange={handleHcrefChange}
              />
              <div className="calculate-button-container">
                <button className="calculate-button" onClick={calculateETref} disabled={loading || !weatherData}>
                  {loading ? t("Calculating...") : t("Calculate WATref")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {results && (
        <div className="results-section">
          <div className="card">
            <div className="card-header">
              <h2>{t("Calculation Results")}</h2>
              <button className="export-button" onClick={exportToPDF}>
                <i className="fas fa-file-pdf"></i> {t("Export to PDF")}
              </button>
            </div>
            <div className="card-body">
              <ResultsTable results={results} />
              <ResultsChart results={results} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Home
